import type { StreamsPayload } from '@painpal/contracts';
import type { AddLayerObject, MapOptions } from 'maplibre-gl';
import { colors } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';

export type LngLat = [number, number];
export type MapStyle = Exclude<MapOptions['style'], string | undefined>;

export interface RouteGeometry { coords: LngLat[]; start: LngLat; bounds: [LngLat, LngLat] }

export const OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export function routeCoordinates(streams: StreamsPayload): LngLat[] {
  const { lat, lng } = streams;
  if (lat === undefined || lng === undefined) return [];
  const coords: LngLat[] = [];
  lat.forEach((latitude, index) => {
    const longitude = lng[index];
    if (latitude !== null && longitude !== null && longitude !== undefined) coords.push([longitude, latitude]);
  });
  return coords;
}

export function routeGeometry(coords: LngLat[]): RouteGeometry | null {
  const start = coords[0];
  if (start === undefined) return null;
  let [minLng, minLat] = start;
  let [maxLng, maxLat] = start;
  for (const [lng, lat] of coords) {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  return { coords, start, bounds: [[minLng, minLat], [maxLng, maxLat]] };
}

export function osmStyle(): MapStyle {
  return {
    version: 8,
    sources: { osm: { type: 'raster', tiles: [OSM_TILES], tileSize: 256, maxzoom: 19, attribution: OSM_ATTRIBUTION } },
    layers: [
      { id: 'paper', type: 'background', paint: { 'background-color': colors.paper2 } },
      { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-saturation': -0.9, 'raster-opacity': 0.55 } },
    ],
  };
}

export function routeFeatures({ coords, start }: RouteGeometry) {
  return {
    route: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } },
    start: { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: start } },
  } as const;
}

const round = { 'line-join': 'round', 'line-cap': 'round' } as const;

const SIZES = {
  desktop: { casing: 9, line: 5, radius: 9, stroke: 3 },
  phone: { casing: 6, line: 3.5, radius: 7, stroke: 2.5 },
} as const;

export function routeLayers(variant: Variant): AddLayerObject[] {
  const size = SIZES[variant];
  return [
    { id: 'route-casing', type: 'line', source: 'route', layout: round, paint: { 'line-color': colors.ink, 'line-width': size.casing } },
    { id: 'route-line', type: 'line', source: 'route', layout: round, paint: { 'line-color': colors.hot, 'line-width': size.line } },
    {
      id: 'route-start', type: 'circle', source: 'start',
      paint: { 'circle-radius': size.radius, 'circle-color': colors.green, 'circle-stroke-color': colors.ink, 'circle-stroke-width': size.stroke },
    },
  ];
}
